// @ts-nocheck
// @ts-ignore
/**
 * SwiperComponent - A web component wrapper for Swiper.js
 *
 * Usage:
 * <swiper-component class="swiper">
 *   <div class="swiper-wrapper">...</div>
 *   <swiper-options>
 *     {
 *       loop: true,
 *       autoplay: true
 *     }
 *   </swiper-options>
 * </swiper-component>
 */
class SwiperComponent extends HTMLElement {
    constructor() {
        super();
        this.swiper = null;
        this._swiperEl = null;
        this._swiperIntersectionObserver = null;
        this._autoPlayingSwiperId = null;
    }

    connectedCallback() {
        // Wait for the next frame to ensure children are parsed and Swiper is likely ready
        if (document.readyState === 'loading' || !window.Swiper || typeof window.Swiper !== 'function' || document.readyState === 'interactive') {
            document.addEventListener('DOMContentLoaded', () => {
                setTimeout(() => {
                    this._setup()
                }, 0)
            });
        } else {
            this._setup()
        }
    }

    _setup() {
        this.init();
    }

    init() {
        if (this.swiper) return;

        if (this._swiperIntersectionObserver) {
            this._swiperIntersectionObserver.disconnect();
            this._swiperIntersectionObserver = null;
        }

        // Determine the actual swiper element.
        // It can be the component itself or a child element with the .swiper class
        const swiperEl = this.classList.contains('swiper') ? this : this.querySelector('.swiper');

        if (!swiperEl) {
            console.warn('SwiperComponent: No element with class "swiper" found inside.', this);
            return;
        }

        this._swiperEl = swiperEl;

        const optionsEl = this.querySelector('swiper-options');
        let options = {};

        if (optionsEl) {
            const optionsContent = optionsEl.textContent.trim();
            const formattedContent = optionsContent.startsWith('{') ? optionsContent : `{${optionsContent}}`;

            try {
                const parser = new Function('component', 'element', `return ${formattedContent}`);
                options = parser.call(this, this, swiperEl);
            } catch (err) {
                console.error('SwiperComponent: Error parsing options.', err, formattedContent);
            }

            optionsEl.style.display = 'none';
        }

        // Manually merge breakpoints settings if they exist.
        // This is needed because some Swiper properties (like 'effect') cannot be changed via breakpoints natively.
        if (options.breakpoints) {
            const windowWidth = window.innerWidthCached;
            const breakpointKeys = Object.keys(options.breakpoints).map(Number).sort((a, b) => a - b);

            let activeBreakpointSettings = {};
            for (const breakpoint of breakpointKeys) {
                if (windowWidth >= breakpoint) {
                    activeBreakpointSettings = options.breakpoints[breakpoint];
                } else {
                    break;
                }
            }

            // Merge the active breakpoint settings into the root options
            options = { ...options, ...activeBreakpointSettings };
        }

        try {
            this.swiper = new Swiper(swiperEl, options);
            if (options.autoplay && this.swiper?.autoplay) {
                this.swiper.on('autoplayStart', () => this._restartProgressAnimation());
                this.swiper.on('autoplayStop', () => this._clearProgressAnimation());
                this.swiper.autoplay.stop();
                this.setupIntersectionObserver();
                this._registerAutoPlayingSwiper();
            }
            this.dispatchEvent(new CustomEvent('swiper:init', {
                detail: { swiper: this.swiper, element: swiperEl },
                bubbles: true
            }));
        } catch (err) {
            console.error('SwiperComponent: Swiper initialization failed.', err);
        }
    }

    _registerAutoPlayingSwiper() {
        const id = this.id || this.getAttribute('data-swiper-id') || `swiper-auto-${Math.random().toString(36).slice(2, 9)}`;
        if (!this.id) {
            this.id = id;
        }

        window.autoPlayingSwiper = window.autoPlayingSwiper || {};
        window.autoPlayingSwiper[id] = this.swiper;
        this._autoPlayingSwiperId = id;
    }

    _unregisterAutoPlayingSwiper() {
        if (!this._autoPlayingSwiperId || !window.autoPlayingSwiper) return;

        delete window.autoPlayingSwiper[this._autoPlayingSwiperId];
        this._autoPlayingSwiperId = null;
    }

    // CSS progress (e.g. banner pagination fill) ends with `forwards` and stays full.
    // Re-toggle so each autoplayStart runs the fill from empty again.
    _restartProgressAnimation() {
        this.classList.remove('is-autoplaying');
        void this.offsetWidth;
        this.classList.add('is-autoplaying');
    }

    _clearProgressAnimation() {
        this.classList.remove('is-autoplaying');
    }

    setupIntersectionObserver() {
        if (this._swiperIntersectionObserver) {
            this._swiperIntersectionObserver.disconnect();
            this._swiperIntersectionObserver = null;
        }

        const waitForFullLoad = this.hasAttribute('data-autoplay-after-load');
        let pageLoaded = document.readyState === 'complete';
        let isIntersecting = false;

        const tryStartAutoplay = () => {
            if (!this.swiper?.autoplay) return;
            if (window.__swiperAutoplayPausedByOverlay) return;
            if (waitForFullLoad && !pageLoaded) return;
            if (!isIntersecting) return;
            if (typeof this.swiper.autoplay.start === 'function') {
                this.swiper.autoplay.start();
            }
        };

        if (waitForFullLoad && !pageLoaded) {
            window.addEventListener(
                'load',
                () => {
                    pageLoaded = true;
                    tryStartAutoplay();
                },
                { once: true }
            );
        }

        const observerCallback = (entries) => {
            const entry = entries[0];
            if (!(this.swiper && this.swiper.autoplay)) return;

            isIntersecting = entry.isIntersecting;
            if (isIntersecting) {
                tryStartAutoplay();
            } else if (typeof this.swiper.autoplay.stop === 'function') {
                this.swiper.autoplay.stop();
            }
        };
        this._swiperIntersectionObserver = new IntersectionObserver(observerCallback, {
            root: null,
            threshold: 0.25 // 25% visible
        });
        this._swiperIntersectionObserver.observe(this._swiperEl);
    }

    disconnectedCallback() {
        this._unregisterAutoPlayingSwiper();

        if (this._swiperIntersectionObserver) {
            this._swiperIntersectionObserver.disconnect();
            this._swiperIntersectionObserver = null;
        }
        if (this.swiper && typeof this.swiper.destroy === 'function') {
            this.swiper.destroy();
            this.swiper = null;
        }
    }

    // Public methods to interact with swiper
    getSwiper() {
        return this.swiper;
    }
}

if (!customElements.get('swiper-component')) {
    customElements.define('swiper-component', SwiperComponent);
}

